/**
 * Shop the Look section.
 * Desktop: horizontal product slider next to (or instead of) the lookbook image.
 * Mobile: horizontal scroll carousel with arrow navigation.
 */
class ShopTheLook extends HTMLElement {
  connectedCallback() {
    this.desktopTrack = this.querySelector("[data-stl-track]");

    // Desktop arrows
    this.querySelectorAll("[data-stl-prev]").forEach((btn) =>
      btn.addEventListener("click", () => this.prev()),
    );
    this.querySelectorAll("[data-stl-next]").forEach((btn) =>
      btn.addEventListener("click", () => this.next()),
    );

    // Desktop color swatches
    this.querySelectorAll("[data-stl-track-slide]").forEach((slide) => {
      slide.querySelectorAll("[data-swatch]").forEach((swatch) => {
        swatch.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.#handleSwatchClick(slide, swatch);
        });
      });
    });

    // Desktop add to cart
    this.querySelectorAll("[data-stl-add-to-cart]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const slide = btn.closest("[data-stl-track-slide]");
        const sizeField = slide?.querySelector("[data-stl-size-field]");
        const variantId = sizeField?.dataset.variantId
          ? parseInt(sizeField.dataset.variantId)
          : parseInt(btn.dataset.variantId);
        if (variantId) this.#addToCart(variantId, btn);
      });
    });

    // Desktop size fields: custom dropdown behavior
    this.querySelectorAll("[data-stl-size-field]").forEach((field) => {
      const trigger = field.querySelector("[data-stl-size-trigger]");
      const label = field.querySelector("[data-stl-size-label]");
      const icon = field.querySelector("[data-stl-size-icon]");
      const menu = field.querySelector("[data-stl-size-menu]");
      const options = field.querySelectorAll("[data-stl-size-option]");
      if (!trigger || !label || !menu) return;

      const updateLabel = () => {
        const activeOption = field.querySelector(
          '[data-stl-size-option][aria-selected="true"]',
        );
        label.textContent = activeOption
          ? activeOption.dataset.sizeLabel || "Select size"
          : "Select size";
        field.dataset.variantId = activeOption?.dataset.variantId || "";
      };

      const setIconOpen = (isOpen) => {
        if (!icon) return;
        icon.style.transform = isOpen ? "rotate(180deg)" : "rotate(0deg)";
        trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
      };

      const closeMenu = () => {
        menu.classList.add("hidden");
        setIconOpen(false);
      };

      const openMenu = () => {
        menu.classList.remove("hidden");
        setIconOpen(true);
      };

      const toggleMenu = () => {
        if (menu.classList.contains("hidden")) openMenu();
        else closeMenu();
      };

      updateLabel();

      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleMenu();
      });

      options.forEach((option) => {
        option.addEventListener("click", (e) => {
          e.preventDefault();
          if (option.disabled) return;

          options.forEach((candidate) =>
            candidate.removeAttribute("aria-selected"),
          );
          option.setAttribute("aria-selected", "true");
          field.dataset.variantId = option.dataset.variantId || "";
          updateLabel();
          closeMenu();
        });
      });

      document.addEventListener("click", (e) => {
        if (!field.contains(e.target)) closeMenu();
      });
    });

    // Mobile scroll arrows
    this.mobileTrack = this.querySelector("[data-stl-mobile-track]");
  }

  next() {
    this.#scrollTrack(this.desktopTrack, "[data-stl-track-slide]", 1);
    this.#scrollTrack(this.mobileTrack, "[data-stl-mobile-slide]", 1);
  }

  prev() {
    this.#scrollTrack(this.desktopTrack, "[data-stl-track-slide]", -1);
    this.#scrollTrack(this.mobileTrack, "[data-stl-mobile-slide]", -1);
  }

  /** Scrolls a horizontal track by one card, wrapping around at the ends. */
  #scrollTrack(track, cardSelector, direction) {
    if (!track) return;
    const card = track.querySelector(cardSelector);
    if (!card) return;

    const gap = parseFloat(getComputedStyle(track).gap) || 40;
    const step = card.offsetWidth + gap;
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll <= 0) return;

    let target = track.scrollLeft + step * direction;
    if (target > maxScroll + 1) target = 0;
    if (target < 0) target = maxScroll;

    track.scrollTo({ left: target, behavior: "smooth" });
  }

  #handleSwatchClick(slide, swatch) {
    const img = slide.querySelector("img");
    if (!img) return;

    if (swatch.dataset.imageSrc) {
      img.src = swatch.dataset.imageSrc;
      if (swatch.dataset.imageSrcset) {
        img.srcset = swatch.dataset.imageSrcset;
      }
    }

    if (swatch.dataset.url) {
      slide.querySelectorAll("a").forEach((a) => (a.href = swatch.dataset.url));
    }

    // Update active swatch
    slide
      .querySelectorAll("[data-swatch]")
      .forEach((s) => s.classList.remove("is-active"));
    swatch.classList.add("is-active");

    // Update add-to-cart variant
    if (swatch.dataset.variantId) {
      const btn = slide.querySelector("[data-stl-add-to-cart]");
      if (btn) btn.dataset.variantId = swatch.dataset.variantId;
    }
  }

  async #addToCart(variantId, btn) {
    const originalText = btn.textContent;
    btn.textContent = "Adding...";
    btn.disabled = true;

    // Pre-order products (custom.preorder metafield) carry a hidden line item
    // property so they can be identified in the cart and on the order.
    const payload = { id: variantId, quantity: 1 };
    if (btn.dataset.preorder === "true") {
      payload.properties = { _preorder: "true" };
    }

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Add to cart failed");
      document.dispatchEvent(new CustomEvent("cart:updated"));
      document.dispatchEvent(new CustomEvent("cart:open"));
    } catch (e) {
      console.error("Add to cart error:", e);
      btn.textContent = "Error";
      setTimeout(() => {
        btn.textContent = originalText.trim();
        btn.disabled = false;
      }, 2000);
      return;
    }

    btn.textContent = originalText.trim();
    btn.disabled = false;
  }
}

customElements.define("shop-the-look", ShopTheLook);

export { ShopTheLook };
